/**
 * Strips HTML tags and decodes HTML entities from a string.
 *
 * This utility removes all HTML markup and converts common HTML entities
 * back to their character equivalents, returning clean text suitable for
 * use as identifiers, plain text display, or API parameters.
 *
 * @param content - The HTML string to strip and decode
 * @returns Clean text with HTML tags removed and entities decoded
 */
export function stripHtmlContent(content: string): string {
  if (!content) {
    return '';
  }

  // Remove HTML tags
  let stripped = content.replace(/<[^>]*>/g, '');

  // Decode HTML entities
  stripped = stripped
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  return stripped.trim();
}

/**
 * Strips HTML tags and decodes HTML entities, then sanitizes the result
 * for use as a DOM element ID or CSS selector.
 *
 * This function removes HTML content and then ensures the resulting string
 * is valid for use as an HTML ID attribute by replacing invalid characters.
 *
 * @param content - The HTML string to process
 * @returns A sanitized string safe for use as an HTML ID
 */
export function stripHtmlForId(content: string): string {
  const stripped = stripHtmlContent(content);

  // Replace spaces and special characters with hyphens, remove consecutive hyphens
  return stripped
    .replace(/[^a-zA-Z0-9\-_]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}
